import { api } from "@/integrations/clients/carAuction/carAuctionClient";
import {
  NewsItem,
  NewsItemResponse,
  CreateNewsRequest,
  UpdateNewsRequest,
  GetAllNewsParams,
  UpdateNewsImageRequest,
  RemoveNewsImageRequest,
  GetNewsResponse,
  CreateNewsResponse,
  UpdateNewsResponse,
  PublishNewsResponse,
  HideNewsResponse,
  DeleteNewsResponse,
} from "@/types/news";
import { FileObject } from "@/types/shared/file";
import { mapUserTypeToTargetUserType } from "@/utils/newsUtils";
import { MediaService } from "@/services/media/mediaService";

export class NewsManagementService {
  // Get all news for admin (including hidden ones) - with pagination support
  static async getAllNews(
    params: GetAllNewsParams & { page?: number; limit?: number },
  ): Promise<{
    items: NewsItem[];
    total: number;
    pagination?: {
      page: number;
      limit: number;
      totalPages: number;
    };
  }> {
    const targetUserType = mapUserTypeToTargetUserType(params.userType);

    // Build params object, only include targetUserType if it's not 'all' (admin sees all by default)
    const requestParams: Record<string, string | number> = {};
    if (params.page) requestParams.page = params.page;
    if (params.limit) requestParams.limit = params.limit;
    // For admin, we can omit targetUserType or use 'all' - backend will return all news
    if (targetUserType && targetUserType !== "all") {
      requestParams.targetUserType = targetUserType;
    }

    const response = await api.get<GetNewsResponse>(`/admin/news`, {
      params: requestParams,
    });

    // api.get already returns response.data, so response is GetNewsResponse directly
    // GetNewsResponse is { success: true, total: number, data: NewsItemResponse[] }
    // So response.data is NewsItemResponse[]
    const items = (response.data || []).map((item) => ({
      ...item,
      createdAt: item.createdAt
        ? typeof item.createdAt === "string"
          ? new Date(item.createdAt)
          : item.createdAt
        : new Date(),
      updatedAt: item.updatedAt
        ? typeof item.updatedAt === "string"
          ? new Date(item.updatedAt)
          : item.updatedAt
        : new Date(),
    })) as NewsItem[];

    return {
      items,
      total: response.total || items.length,
      pagination: response.pagination,
    };
  }

  // Create a new news item
  static async createNews(
    request: CreateNewsRequest,
    createdBy: string,
  ): Promise<NewsItem> {
    // Step 1: Create news first (without mediaId)
    // api.post already returns response.data, so response is CreateNewsResponse directly
    const response = await api.post<CreateNewsResponse>("/admin/news", {
      title: request.title,
      content: request.content,
      mediaId: undefined,
      targetUserType: request.targetUserType,
      specificUserTargets: request.specificUserTargets,
      labels: request.labels,
    });

    // response is { success: true, data: NewsDto }
    // So response.data is NewsDto which has id
    const newsId = response.data.id;

    // Step 2: Handle image upload via S3 if image is provided
    // For News, we create both PREVIEW (100kb) and FULL_SIZE (0.7mb) versions
    // This allows listings/carousels to use small preview images while detail views use fullSize
    if (request.image && newsId) {
      try {
        const { compressImageForPreview, compressImageForFullSize } =
          await import("@/utils/imageCompression");

        // Create preview version (100kb max)
        let previewFile: File;
        let fullSizeFile: File;

        try {
          previewFile = await compressImageForPreview(request.image);
          // Create fullSize version (0.7mb max)
          fullSizeFile = await compressImageForFullSize(request.image);
        } catch (compressionError) {
          // Fallback to original file if compression fails
          previewFile = request.image;
          fullSizeFile = request.image;
        }

        // Step 3: Upload preview version to S3 and create Media record
        const fileExtension = request.image.name.split(".").pop() || "jpg";
        const previewFileName = `${request.image.name.replace(/\.[^/.]+$/, "")}_preview.${fileExtension}`;

        try {
          const { uploadUrl: previewUploadUrl, s3Key: previewS3Key } =
            await MediaService.requestPresignedUploadUrl(
              "news",
              newsId,
              {
                originalName: previewFileName,
                mimeType: previewFile.type,
                sizeBytes: previewFile.size,
              },
              "preview", // subPath with preview suffix
            );

          await MediaService.uploadFileToS3(previewUploadUrl, previewFile);

          const previewMediaResponse =
            await MediaService.createMediaAfterUpload(
              previewS3Key,
              {
                originalName: previewFileName,
                mimeType: previewFile.type,
                sizeBytes: previewFile.size,
              },
              { newsId, mediaSize: "PREVIEW" },
            );

          await MediaService.updateMediaStatus(
            previewMediaResponse.id,
            "UPLOADED",
          );
        } catch (previewError) {
          // Continue with fullSize upload even if preview fails
        }

        // Step 4: Upload fullSize version to S3 and create Media record
        const fullSizeFileName = `${request.image.name.replace(/\.[^/.]+$/, "")}_fullsize.${fileExtension}`;

        try {
          const { uploadUrl: fullSizeUploadUrl, s3Key: fullSizeS3Key } =
            await MediaService.requestPresignedUploadUrl(
              "news",
              newsId,
              {
                originalName: fullSizeFileName,
                mimeType: fullSizeFile.type,
                sizeBytes: fullSizeFile.size,
              },
              "fullsize", // subPath with fullsize suffix
            );

          await MediaService.uploadFileToS3(fullSizeUploadUrl, fullSizeFile);

          const fullSizeMediaResponse =
            await MediaService.createMediaAfterUpload(
              fullSizeS3Key,
              {
                originalName: fullSizeFileName,
                mimeType: fullSizeFile.type,
                sizeBytes: fullSizeFile.size,
              },
              { newsId, mediaSize: "FULL_SIZE" },
            );

          await MediaService.updateMediaStatus(
            fullSizeMediaResponse.id,
            "UPLOADED",
          );
        } catch (fullSizeError) {
          // Continue even if fullSize fails - at least we tried
        }

        // Note: Media.newsId FK is already set, so News.imageMedia relation is automatically updated by Prisma
        // No need to update News with mediaId - relation is already established
      } catch (error) {
        // Continue without image - news is already created
      }
    }

    // Fetch updated news to get the image URL (presigned URL will be generated by backend)
    // api.get already returns response.data, so finalResponse is GetNewsItemResponse directly
    // GetNewsItemResponse is { success: true, data: NewsDto | null }
    const finalResponse = await api.get<{
      success: boolean;
      data: NewsItemResponse | null;
    }>(`/admin/news/${newsId}`);

    if (!finalResponse || !finalResponse.data) {
      throw new Error("Failed to fetch created news item");
    }

    const newsData = finalResponse.data;

    // Send notification emails after image upload (if any) so hero image is included
    try {
      await api.post<{ success: boolean; count: number }>(
        `/admin/news/${newsId}/send-notifications`,
      );
    } catch {
      // News is created; email send is best-effort
    }

    return {
      ...newsData,
      createdAt: newsData.createdAt
        ? typeof newsData.createdAt === "string"
          ? new Date(newsData.createdAt)
          : newsData.createdAt
        : new Date(),
      updatedAt: newsData.updatedAt
        ? typeof newsData.updatedAt === "string"
          ? new Date(newsData.updatedAt)
          : newsData.updatedAt
        : new Date(),
    } as NewsItem;
  }

  // Publish a news item (make it visible)
  static async publishNews(
    newsId: string,
  ): Promise<{ status: string; message: string }> {
    const response = await api.post<PublishNewsResponse>(
      `/admin/news/${newsId}/publish`,
    );

    return {
      status: "success",
      message: response.message || "Nyhet publicerad framgångsrikt",
    };
  }

  // Hide a news item (make it invisible)
  static async hideNews(
    newsId: string,
  ): Promise<{ status: string; message: string }> {
    const response = await api.post<HideNewsResponse>(
      `/admin/news/${newsId}/hide`,
    );

    return {
      status: "success",
      message: response.message || "Nyhet dold framgångsrikt",
    };
  }

  static async updateNews(
    request: UpdateNewsRequest,
    createdBy?: string,
  ): Promise<NewsItem> {
    const { id, ...updateData } = request;
    let mediaId: string | null | undefined = undefined;

    // Handle image update
    // If image.id exists, it's an existing image - use the id
    // If image is null, remove the image
    // If image is undefined, don't change mediaId
    if (updateData.image === null) {
      mediaId = null; // Remove image
    } else if (updateData.image?.id) {
      mediaId = updateData.image.id; // Use existing image
    }
    // If image is undefined, mediaId stays undefined (no change)

    // api.put already returns response.data, so response is UpdateNewsResponse directly
    // UpdateNewsResponse is { success: true, data: NewsItemResponse }
    // So response.data is NewsItemResponse
    const response = await api.put<UpdateNewsResponse>(`/admin/news/${id}`, {
      title: updateData.title,
      content: updateData.content,
      mediaId: mediaId,
      targetUserType: updateData.targetUserType,
      specificUserTargets: updateData.specificUserTargets,
      labels: updateData.labels,
    });

    const newsData = response.data;
    return {
      ...newsData,
      createdAt: newsData.createdAt
        ? typeof newsData.createdAt === "string"
          ? new Date(newsData.createdAt)
          : newsData.createdAt
        : new Date(),
      updatedAt: newsData.updatedAt
        ? typeof newsData.updatedAt === "string"
          ? new Date(newsData.updatedAt)
          : newsData.updatedAt
        : new Date(),
    } as NewsItem;
  }

  // Remove a news item
  static async removeNews(
    newsId: string,
  ): Promise<{ status: string; message: string }> {
    const response = await api.delete<DeleteNewsResponse>(
      `/admin/news/${newsId}`,
    );

    return {
      status: "success",
      message: response.message || "Nyhet raderad framgångsrikt",
    };
  }

  // Update news image (admin functionality) - uploads new image via S3
  // For News, we create both PREVIEW (100kb) and FULL_SIZE (0.7mb) versions
  static async updateNewsImage(
    newsId: string,
    request: UpdateNewsImageRequest,
    createdBy: string,
  ): Promise<FileObject> {
    // Step 1: Delete ALL existing images if they exist (both preview and fullSize)
    // Delete all media records for this news
    try {
      await api.delete(`/media/news/${newsId}`);
    } catch (error) {
      // Continue with upload even if deletion fails
    }

    // Step 2: Create both preview and fullSize versions
    const { compressImageForPreview, compressImageForFullSize } =
      await import("@/utils/imageCompression");

    let previewFile: File;
    let fullSizeFile: File;

    try {
      previewFile = await compressImageForPreview(request.image);
      fullSizeFile = await compressImageForFullSize(request.image);
    } catch (compressionError) {
      previewFile = request.image;
      fullSizeFile = request.image;
    }

    // Step 3: Upload preview version to S3 and create Media record
    const fileExtension = request.image.name.split(".").pop() || "jpg";
    const previewFileName = `${request.image.name.replace(/\.[^/.]+$/, "")}_preview.${fileExtension}`;

    try {
      const { uploadUrl: previewUploadUrl, s3Key: previewS3Key } =
        await MediaService.requestPresignedUploadUrl(
          "news",
          newsId,
          {
            originalName: previewFileName,
            mimeType: previewFile.type,
            sizeBytes: previewFile.size,
          },
          "preview", // subPath with preview suffix
        );

      await MediaService.uploadFileToS3(previewUploadUrl, previewFile);

      const previewMediaResponse = await MediaService.createMediaAfterUpload(
        previewS3Key,
        {
          originalName: previewFileName,
          mimeType: previewFile.type,
          sizeBytes: previewFile.size,
        },
        { newsId, mediaSize: "PREVIEW" },
      );

      await MediaService.updateMediaStatus(previewMediaResponse.id, "UPLOADED");
    } catch (previewError) {
      // Continue with fullSize upload even if preview fails
    }

    // Step 4: Upload fullSize version to S3 and create Media record
    const fullSizeFileName = `${request.image.name.replace(/\.[^/.]+$/, "")}_fullsize.${fileExtension}`;

    try {
      const { uploadUrl: fullSizeUploadUrl, s3Key: fullSizeS3Key } =
        await MediaService.requestPresignedUploadUrl(
          "news",
          newsId,
          {
            originalName: fullSizeFileName,
            mimeType: fullSizeFile.type,
            sizeBytes: fullSizeFile.size,
          },
          "fullsize", // subPath with fullsize suffix
        );

      await MediaService.uploadFileToS3(fullSizeUploadUrl, fullSizeFile);

      const fullSizeMediaResponse = await MediaService.createMediaAfterUpload(
        fullSizeS3Key,
        {
          originalName: fullSizeFileName,
          mimeType: fullSizeFile.type,
          sizeBytes: fullSizeFile.size,
        },
        { newsId, mediaSize: "FULL_SIZE" },
      );

      await MediaService.updateMediaStatus(
        fullSizeMediaResponse.id,
        "UPLOADED",
      );
    } catch (fullSizeError) {
      // Continue even if fullSize fails - at least we tried
    }

    // Fetch updated news to get presigned URL
    // api.get already returns response.data, so updatedNews is GetNewsItemResponse directly
    // GetNewsItemResponse is { success: true, data: NewsDto | null }
    // So updatedNews.data is NewsDto | null
    const updatedNews = await api.get<{
      success: boolean;
      data: NewsItemResponse | null;
    }>(`/admin/news/${newsId}`);

    if (!updatedNews.data) {
      // Fallback if news not found - use fullSize mediaId if available, otherwise empty
      return {
        id: "",
        url: "",
        name: request.image.name,
        type: request.image.type,
        size: request.image.size,
      };
    }

    return (
      updatedNews.data.image || {
        id: "",
        url: "",
        name: request.image.name,
        type: request.image.type,
        size: request.image.size,
      }
    );
  }

  // Remove news image (admin functionality)
  static async removeNewsImage(request: RemoveNewsImageRequest): Promise<void> {
    await api.delete(`/admin/news/${request.newsId}/image`);
  }
}
